import * as THREE from 'three';
import type { EmissiveGroup } from '../assets/catalog';
import type { WorldState } from '../world/state/types';
import { forEachMarkedMesh } from './markup';

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
const smooth = (t: number): number => t * t * (3 - 2 * t);

/** 0 in daylight, ramps on as the sun goes from +2° to -1°. */
export function lanternLevel(sunAltDeg: number): number {
  return smooth(clamp01((2 - sunAltDeg) / 3));
}

export function emissiveLevel(group: EmissiveGroup, state: WorldState): number {
  switch (group) {
    case 'lantern':
      return lanternLevel(state.sky.sun.altDeg);
    case 'window':
      return lanternLevel(state.sky.sun.altDeg - 2);
    case 'vending':
      return 1; // vending machines glow day and night
  }
}

export const EMISSIVE_COLOR: Record<EmissiveGroup, number> = { lantern: 0xffb566, window: 0xffd9a0, vending: 0xe8f4ff };
const PEAK_INTENSITY: Record<EmissiveGroup, number> = { lantern: 4, window: 2.5, vending: 2 };

/** Drives every mesh marked `emissive` from WorldState. Materials are cloned so groups never share state. */
export class EmissiveController {
  /** Meshes, not materials: later material patches (snow, aerial) swap in clones and must stay controlled. */
  private readonly entries: Array<{ group: EmissiveGroup; mesh: THREE.Mesh }> = [];

  collect(root: THREE.Object3D): void {
    forEachMarkedMesh(root, 'emissive', (mesh, value) => {
      if (!(mesh.material instanceof THREE.MeshStandardMaterial)) return;
      const group = value as EmissiveGroup;
      const material = mesh.material.clone();
      material.emissive.setHex(EMISSIVE_COLOR[group]);
      mesh.material = material;
      this.entries.push({ group, mesh });
    });
  }

  /** compensation: emissiveCompensation(exposure) from Lighting, so lamps stay steady while the eye adapts. */
  update(state: WorldState, compensation = 1): void {
    for (const { group, mesh } of this.entries) {
      const m = mesh.material as THREE.MeshStandardMaterial;
      m.emissiveIntensity = emissiveLevel(group, state) * PEAK_INTENSITY[group] * compensation;
    }
  }

  get count(): number {
    return this.entries.length;
  }

  /** World positions of the meshes of one group (for real point lights). */
  positions(group: EmissiveGroup): THREE.Vector3[] {
    return this.entries.filter((e) => e.group === group).map((e) => e.mesh.getWorldPosition(new THREE.Vector3()));
  }
}
